import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { FormGroup, Button, Intent, Switch, Spinner } from "@blueprintjs/core";
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import { FormSection, FieldError, MoreOptions } from 'components/FormBits';
import TaxXeroModal from 'components/Taxes/TaxXeroModal';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// Bring a field with an error into view and put the cursor in it.
const focusField = (id) => {
  setTimeout(() => {
    const field = document.getElementById(id);
    if (!field) return;
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field.focus({ preventScroll: true });
  }, 0);
};

@inject('taxStore', 'quickbooksStore','xeroStore') @observer
class TaxModal extends Component {
  @observable isTaxXeroDialogVisible = false;
  @observable errors = {};

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.errors = {};
    }
  }

  clearError(key) {
    if (this.errors[key]) {
      this.errors = { ...this.errors, [key]: null };
    }
  }

  // Name and percentage are required: errors under each, focus the first.
  validate() {
    const { object } = this.props.taxStore.tax;
    const errors = {};
    if (isBlank(object.name)) errors.name = 'Enter a name, e.g. Sales tax.';
    if (isBlank(object.percentage_value)) errors.percentage_value = 'Enter the rate, e.g. 8.25.';
    this.errors = errors;
    if (errors.name) focusField('tax-name');
    else if (errors.percentage_value) focusField('tax-percentage');
    return !errors.name && !errors.percentage_value;
  }

  handleCloseDailog() {
    // clearing object
    this.props.taxStore.tax.object = {}
    this.props.onClose()
  }

  updateTax() {
    let { taxStore } = this.props;
     
    if (!this.validate()) {
      return;
    }
    taxStore.update()
      .then(this.onSuccessfullyUpdateTax.bind(this))
      .catch(this.onFailCreateTax.bind(this))
  }

  onCloseTaxXeroDialog() {
    this.isTaxXeroDialogVisible = false;
  }

  openXeroTaxModal() {
    let { taxStore } = this.props;
    this.props.taxStore.tax.object.tax_type = "OUTPUT";

    if (!this.validate()) {
      return;
    }
    
    taxStore.createTaxOnXero()
      .then(this.onSuccessfullyCreateTaxZero.bind(this))
      .catch(this.onFailCreateTaxZero.bind(this))
  }

  onSuccessfullyCreateTaxZero(response) {
    this.props.xeroStore.loadXeroTaxCodes();
    Toast.show({ message: "Tax created successfully.", intent: Intent.SUCCESS })
  }

  onFailCreateTaxZero(error) {
    if(error.response && error.response.data && error.response.data.errors) {
      Toast.show({ message: error.response.data.errors, intent: Intent.DANGER })
    } else {
      Toast.show({ message: "Something went wrong. Failed to create/update tax", intent: Intent.DANGER })
    }
  }

  onSucessUpdateTaxXero() {
    this.isTaxXeroDialogVisible = true;
  }

  onSuccessfullyUpdateTax(response) {
    this.props.taxStore.tax.object = {} // so the next Add starts blank
    this.props.onSuccess()
    Toast.show({ message: "Tax updated successfully.", intent: Intent.SUCCESS })
  }

  createTax() {
    let { taxStore } = this.props;

    if (!this.validate()) {
      return;
    }
    taxStore.create()
      .then(this.onSuccessfullyCreateTax.bind(this))
      .catch(this.onFailCreateTax.bind(this))
  }

  onSuccessfullyCreateTax(response) {
    this.props.taxStore.tax.object = {} // so the next Add starts blank
    this.props.onSuccess()
    Toast.show({ message: "Tax created successfully.", intent: Intent.SUCCESS })
  }

  onFailCreateTax(error) {
    if(error.response && error.response.data && error.response.data.errors) {
      Toast.show({ message: error.response.data.errors, intent: Intent.DANGER })
    } else {
      Toast.show({ message: "Something went wrong. Failed to create/update tax", intent: Intent.DANGER })
    }
  }

  render() {
    let {
      edit,
      taxStore: {
        tax: {
          isLoading,
          object
        }
      },
      quickbooksStore: {
        quickbooksTaxCodes: {
          objects
        }
      },
      xeroStore: {
        xeroTaxCodes
      }
    } = this.props;
    const busy = !!isLoading;
    const close = this.handleCloseDailog.bind(this);
    const errors = this.errors;
    const hasQuickbooks = !!(objects && objects.length > 0);
    const hasXero = !!(xeroTaxCodes.objects && xeroTaxCodes.objects.length > 0);

    return(
      <div>
        <FormDrawer
          title={edit ? "Edit tax" : "New tax"}
          icon={edit ? "edit" : "percentage"}
          isOpen={this.props.isOpen}
          onClose={close}
          busy={busy}
          footer={
            <>
              <Button text="Cancel" onClick={close} disabled={busy} />
              {edit ?
                <Button
                  intent={Intent.PRIMARY}
                  icon="floppy-disk"
                  text="Save changes"
                  loading={busy}
                  onClick={this.updateTax.bind(this)}
                />
                :
                <Button
                  intent={Intent.PRIMARY}
                  icon="add"
                  text="Create tax"
                  loading={busy}
                  onClick={this.createTax.bind(this)}
                />
              }
            </>
          }
        >
          <FormSection title="Tax" hint="The name and rate, as they appear on estimates and invoices.">
            <div className="row">
              <div className="col-7">
                <FormGroup
                  className={errors.name ? 'has-error' : undefined}
                  label="Name"
                  labelFor="tax-name"
                  labelInfo="*"
                >
                  <input
                    id="tax-name"
                    type='text'
                    className='bp3-input bp3-fill'
                    placeholder='e.g. Sales tax'
                    defaultValue={object.name}
                    aria-invalid={!!errors.name}
                    onChange={(event) => { object.name = event.target.value; this.clearError('name'); } }
                  />
                  <FieldError>{errors.name}</FieldError>
                </FormGroup>
              </div>
              <div className="col-5">
                <FormGroup
                  className={errors.percentage_value ? 'has-error' : undefined}
                  label="Rate (%)"
                  labelFor="tax-percentage"
                  labelInfo="*"
                >
                  <input
                    id="tax-percentage"
                    type='number'
                    step="any"
                    inputMode="decimal"
                    className='bp3-input bp3-fill'
                    placeholder='e.g. 8.25'
                    defaultValue={object.percentage_value || ""}
                    aria-invalid={!!errors.percentage_value}
                    onChange={(event) => { object.percentage_value = event.target.value; this.clearError('percentage_value'); } }
                  />
                  <FieldError>{errors.percentage_value}</FieldError>
                </FormGroup>
              </div>
            </div>

            <FormGroup>
              <Switch
                label="Default tax"
                checked={!!object.is_default}
                onChange={(event) => { object.is_default = event.target.checked }}
              />
            </FormGroup>
          </FormSection>

          {(hasQuickbooks || hasXero) &&
            <MoreOptions
              label="Accounting"
              count={(hasQuickbooks ? 1 : 0) + (hasXero ? 1 : 0)}
              open={!isBlank(object.quickbooks_tax_code_id) || !isBlank(object.xero_tax_code_name)}
            >
              {hasQuickbooks ?
                <FormGroup
                  label="QuickBooks tax code"
                  labelFor="quickbooks-tax-code"
                  helperText={
                    <a href="https://quickbooks.intuit.com/" target="_blank" rel="noopener noreferrer">
                      Add to QuickBooks
                    </a>
                  }
                >
                  {this.props.quickbooksStore.quickbooksTaxCodes.isLoading && <Spinner size={Spinner.SIZE_SMALL} />}
                  {!this.props.quickbooksStore.quickbooksTaxCodes.isLoading && <div className="bp3-select bp3-fill">
                    <select
                      id="quickbooks-tax-code"
                      value={object.quickbooks_tax_code_id || ''}
                      onChange={event => {
                        object.quickbooks_tax_code_id = event.target.value;
                      }}
                    >
                      <option value="">Choose a QuickBooks tax code</option>
                      {objects.map((taxCode) => {
                        return(
                          <option value={taxCode.id} key={taxCode.id}>{taxCode.name}</option>
                        )
                      })}
                    </select>
                  </div>}
                </FormGroup>
                : null
              }

              {hasXero ?
                <FormGroup label="Xero tax rate" labelFor="xero-tax-code">
                  {this.props.xeroStore.xeroTaxCodes.isLoading && <Spinner size={Spinner.SIZE_SMALL} />}
                  {!this.props.xeroStore.xeroTaxCodes.isLoading && <div className="bp3-select bp3-fill">
                    <select
                      id="xero-tax-code"
                      value={object.xero_tax_code_name || ''}
                      onChange={event => {
                        object.xero_tax_code_name = event.target.value;
                      }}
                    >
                      <option value="">Choose a Xero tax rate</option>
                      {xeroTaxCodes.objects.map((taxCode, index) => {
                        return(
                          <option value={taxCode.TaxType} key={`${taxCode.TaxType}-${index}`}>{taxCode.Name}</option>
                        )
                      })}
                    </select>
                  </div>}
                  <Button
                    className="mt-2"
                    small={true}
                    icon="add"
                    loading={xeroTaxCodes.isLoading}
                    onClick={this.openXeroTaxModal.bind(this)}
                  >
                    Add to Xero
                  </Button>
                </FormGroup>
                : null
              }
            </MoreOptions>
          }
        </FormDrawer>
        <TaxXeroModal 
          isOpen={this.isTaxXeroDialogVisible} 
          title={"Add Tax to Xero"}
          onClose={this.onCloseTaxXeroDialog.bind(this)}
          onSuccess={this.onSucessUpdateTaxXero.bind(this)}
        />
      </div>
    )
  }
};

export default TaxModal;
